import { useState, useCallback, useEffect } from 'react';
import { cva } from 'class-variance-authority';
import { Alert, Button } from '@nula/core-ui';
import { useDocAccessDeniedStatus } from '@/lib/sync/hooks/use-doc-access-denied-status';
import styles from './doc-access-denied-banner.module.css';

const docAccessDeniedBanner = cva(styles.base);

interface Props {
  entityId: string;
}

/**
 * DocAccessDeniedBanner — shown when the server denies access to the active
 * document, either in response to a subscribe request (new navigation) or
 * as a spontaneous revocation of an already-subscribed doc.
 *
 * - Subscribes to docAccessDeniedBus via useDocAccessDeniedStatus
 * - Dismissible (the sync layer has already unsubscribed server-side)
 * - Full-width banner using core-ui Alert with type="error"
 */
export function DocAccessDeniedBanner({ entityId }: Props) {
  const status = useDocAccessDeniedStatus(entityId);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    setDismissed(false);
  }, [entityId]);

  const handleDismiss = useCallback(() => {
    setDismissed(true);
  }, []);

  useEffect(() => {
    const isVisible = status.state === 'denied' && !dismissed;
    if (!isVisible) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleDismiss();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [status.state, dismissed, handleDismiss]);

  if (status.state !== 'denied' || dismissed) return null;

  return (
    <Alert
      type="error"
      banner
      showIcon
      message="You don't have access to this document. The owner may have revoked your permission."
      action={
        <Button
          variant="text"
          size="sm"
          aria-label="Dismiss access denied warning"
          onClick={handleDismiss}
        >
          Dismiss
        </Button>
      }
      className={docAccessDeniedBanner()}
    />
  );
}
